"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import GradientChevron from "../../icons/GradientChevron"
import ArrowDown from "../../icons/ArrowDown"
import {
  Rail,
  AnchorItem,
  AnchorText,
  AnchorChevron,
  MobileRoot,
  MobileBar,
  MobileBarText,
  MobileCaret,
  MobilePanel,
  MobileRow,
  MobileRowText,
} from "./styles"

// EGDE-475: page-content wrapper that reserves the rail's gutter on desktop.
// Re-exported through this "use client" module so the server-side PageBlocks
// can render it as a client reference.
export { RailPageContent } from "./styles"

export interface AnchorLink {
  id: string
  label: string
}

interface Props {
  anchors: AnchorLink[]
}

// Distance below the sticky header where a section is considered "active".
// Kept in sync with the `scroll-margin-top` on the Rich Content block Root so
// the spy and the smooth-scroll landing agree.
const OFFSET = 120

/**
 * EGDE-76: page-level anchor navigation for Rich Content Blocks.
 *
 * Each Rich Content Block carries a single anchor (its `anchorId`, rendered as
 * the block's element id, with the block's `sectionTitle` as the label).
 *
 * Desktop: a fixed rail listing every anchor on the page (unchanged).
 * Mobile (EGDE-341): the old always-fixed top strip covered the whole page and
 * QA flagged it as "in the way". It now mirrors the magazine article's mobile
 * TOC (EGDE-392/393): a collapsible bar pinned to the bottom of the viewport,
 * visible only while the rich-content region is on screen.
 */
export default function RichContentAnchorNav({ anchors }: Props) {
  const [activeId, setActiveId] = useState<string>(anchors[0]?.id ?? "")
  const [open, setOpen] = useState(false)
  const [visible, setVisible] = useState(false)
  // EGDE-475 (round 3): the desktop rail hides while the footer occupies its band.
  const [railVisible, setRailVisible] = useState(true)
  const railRef = useRef<HTMLElement | null>(null)
  const isScrollingRef = useRef(false)

  useEffect(() => {
    if (anchors.length === 0) return

    const handleScroll = () => {
      // The mobile bar shows only while the rich-content region (first block
      // top → last block bottom) is on screen, like the magazine article bar
      // (same 80px header inset as its IntersectionObserver rootMargin).
      let top = Infinity
      let bottom = -Infinity
      for (const a of anchors) {
        const el = document.getElementById(a.id)
        if (!el) continue
        const r = el.getBoundingClientRect()
        top = Math.min(top, r.top)
        bottom = Math.max(bottom, r.bottom)
      }
      const vh = window.innerHeight || document.documentElement.clientHeight
      const regionOnScreen =
        top !== Infinity && bottom > 80 && top < vh
      setVisible(regionOnScreen)
      if (!regionOnScreen) setOpen(false)

      // EGDE-475 (round 3): the fixed desktop rail must never paint over the
      // footer — fade it out once the footer's top crosses the rail's bottom.
      const footer = document.querySelector("[data-component='footer']")
      const railBottom =
        railRef.current?.getBoundingClientRect().bottom ?? vh * 0.85
      setRailVisible(
        !footer || footer.getBoundingClientRect().top > railBottom + 16,
      )

      // Don't fight a click-initiated smooth scroll.
      if (isScrollingRef.current) return
      let current = anchors[0].id
      for (const a of anchors) {
        const el = document.getElementById(a.id)
        if (el && el.getBoundingClientRect().top <= OFFSET) current = a.id
      }
      setActiveId(current)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleScroll)
    handleScroll()
    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleScroll)
    }
  }, [anchors])

  const handleClick = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault()
    setActiveId(id)
    setOpen(false)
    isScrollingRef.current = true
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
      setTimeout(() => {
        isScrollingRef.current = false
      }, 1000)
    }
  }, [])

  if (anchors.length === 0) return null

  const activeAnchor = anchors.find((a) => a.id === activeId) ?? anchors[0]
  // EGDE-485: a single anchor has no hierarchy to expand — the collapsible
  // would just reveal a second copy of the same title. Render the mobile bar
  // as a direct scroll button instead, mirroring the desktop rail item.
  const single = anchors.length === 1

  return (
    <>
      <Rail ref={railRef} aria-label="Section navigation" $visible={railVisible}>
        {anchors.map((a) => {
          const isActive = a.id === activeId
          return (
            <AnchorItem
              key={a.id}
              href={`#${a.id}`}
              $active={isActive}
              onClick={(e) => handleClick(e, a.id)}
            >
              <AnchorChevron>
                <GradientChevron width={12} height={20} />
              </AnchorChevron>
              <AnchorText variant="body1" $active={isActive}>
                {a.label}
              </AnchorText>
            </AnchorItem>
          )
        })}
      </Rail>

      <MobileRoot $visible={visible}>
        <MobileBar
          type="button"
          aria-expanded={single ? undefined : open}
          onClick={
            single
              ? (e) => handleClick(e, anchors[0].id)
              : () => setOpen((prev) => !prev)
          }
        >
          <AnchorChevron>
            <GradientChevron width={12} height={20} />
          </AnchorChevron>
          <MobileBarText variant="body1">{activeAnchor.label}</MobileBarText>
          {!single && (
            <MobileCaret $open={open}>
              <ArrowDown width={16} height={8} />
            </MobileCaret>
          )}
        </MobileBar>

        {!single && open && (
          <MobilePanel>
            {anchors.map((a) => {
              const isActive = a.id === activeId
              return (
                <MobileRow
                  key={a.id}
                  href={`#${a.id}`}
                  $active={isActive}
                  onClick={(e) => handleClick(e, a.id)}
                >
                  <AnchorChevron>
                    <GradientChevron width={12} height={20} />
                  </AnchorChevron>
                  <MobileRowText variant="body1" $active={isActive}>
                    {a.label}
                  </MobileRowText>
                </MobileRow>
              )
            })}
          </MobilePanel>
        )}
      </MobileRoot>
    </>
  )
}
